import React from 'react';
import { InputAdornment, TextField, TextFieldVariants } from '@mui/material';
import { Field, FieldAttributes, FieldProps, useFormikContext } from 'formik';
import { IconButton } from 'ui/buttons/IconButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface PasswordFieldProps {
    label?: React.ReactNode;
    placeholder?: string;
    disabled?: boolean;
    inputRef?: React.Ref<HTMLInputElement>;
    inputProps?: Record<string, any>;
    description?: React.ReactNode;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    className?: string;
    error?: string;
    readOnly?: boolean;
    shrink?: boolean;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    variant?: TextFieldVariants;
    size?: 'small' | 'medium';
    showHelperText?: boolean;
}

export const PasswordFormField: React.FC<FieldAttributes<PasswordFieldProps>> = ({
    label,
    placeholder,
    description,
    disabled,
    inputRef,
    inputProps,
    readOnly,
    onKeyDown,
    onClick,
    error,
    className,
    shrink,
    variant,
    size,
    showHelperText,
    ...props
}) => (
    <Field {...props}>
        {(childProps: FieldProps) => (
            <PasswordFormFieldComponent
                label={label}
                placeholder={placeholder}
                disabled={disabled}
                inputRef={inputRef}
                inputProps={inputProps}
                description={description}
                onClick={onClick}
                error={error}
                shrink={shrink}
                onKeyDown={onKeyDown}
                className={className}
                readOnly={readOnly}
                variant={variant}
                size={size}
                showHelperText={showHelperText}
                {...childProps}
            />
        )}
    </Field>
);

const PasswordFormFieldComponent: React.FC<FieldProps & PasswordFieldProps> = ({
    field,
    meta,
    label,
    placeholder,
    description,
    disabled,
    inputRef,
    onKeyDown,
    onClick,
    error,
    className,
    readOnly,
    shrink,
    variant,
    showHelperText = true,
    size
}) => {
    const { isSubmitting } = useFormikContext();

    const [isVisible, setIsVisible] = React.useState(false);
    const toggleVisibility = React.useCallback(() => {
        setIsVisible(!isVisible);
    }, [isVisible]);

    const isError = React.useMemo(
        () => meta.touched && (!!meta.error || !!error),
        [error, meta.error, meta.touched]
    );
    const helperText = React.useMemo(() => {
        if (!showHelperText) {
            return null;
        }
        return isError ? error || meta.error : description;
    }, [description, error, isError, meta.error, showHelperText]);

    return (
        <TextField
            fullWidth
            type={isVisible ? 'text' : 'password'}
            placeholder={placeholder}
            name={field.name}
            label={label}
            disabled={disabled || isSubmitting}
            inputRef={inputRef}
            value={field.value ?? ''}
            onChange={field.onChange}
            InputLabelProps={{ shrink }}
            onBlur={field.onBlur}
            onKeyDown={onKeyDown}
            onClick={onClick}
            error={isError}
            helperText={helperText}
            className={className}
            variant={variant}
            size={size}
            InputProps={{
                readOnly,
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={toggleVisibility}>
                            {isVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                )
            }}
        />
    );
};
